// @ts-nocheck -- Transitional typing for a legacy JavaScript module.
import { useState } from "react";
import { Link } from "react-router-dom";
import { useGetDeals, useUnarchiveDeal, useDeleteDeal } from "@/hooks/useDeals";
import { Archive, ChevronLeft, ArchiveRestore, Trash2 } from "lucide-react";
import EmptyState from "@/components/common/EmptyState";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import PageHeader from "@/components/layout/PageHeader";
import ArchivedDealsSkeleton from "@/components/deals/skeletons/ArchivedDealsSkeleton";
import { Button } from "@/components/ui/button";
import { DealType } from "@/enum/common.enum";
import { PROTECTED_ROUTES } from "@/routes/url";

/**
 * ArchivedDeals — the "recycle bin" surface for the studio.
 *
 * Lists deals soft-archived via DealDetail's danger zone. From here an
 * org admin / agent can either Restore (unarchive) or Delete permanently
 * (typed-confirmation modal). Externals never reach this page — the
 * dashboard route is internal-only.
 */
export default function ArchivedDeals() {
  const { data: dealsData, isLoading: loading, refetch } = useGetDeals({ archived: true, limit: 500 });
  const deals = dealsData?.deals ?? [];
  const [confirmTarget, setConfirmTarget] = useState(null);
  const { mutate: unarchiveDeal } = useUnarchiveDeal();

  const load = () => { refetch(); };

  const restore = (deal) => {
    unarchiveDeal(deal.id);
  };

  return (
    <div className="font-dashboard" data-testid="archived-page">
      <Link
          to={PROTECTED_ROUTES.DASHBOARD}
          className="mb-4 inline-flex items-center gap-1 text-sm text-dash-muted hover:text-dash-greenDeep"
          data-testid="archived-back-to-dashboard"
        >
          <ChevronLeft className="size-3.5" /> Back to deals
        </Link>
        <PageHeader className="mb-8" eyebrow="Archive" title="Archived deals" description="Deals here are hidden from your dashboard and don’t count toward your plan’s active-deal limit. Restore any time—or delete permanently once you’re sure." />

        {loading ? (
          <ArchivedDealsSkeleton />
        ) : deals.length === 0 ? (
          <EmptyState icon={Archive} title="Nothing archived" description="Archived deals will collect here—safe, out of the way, and ready to restore whenever you need them." data-testid="archived-empty" />
        ) : (
          <ul className="divide-y divide-dash-border overflow-hidden rounded-xl border border-dash-border bg-white" data-testid="archived-list">
            {deals.map((d) => (
              <li key={d.id} className="flex flex-wrap items-center gap-4 px-4 py-4 sm:gap-6 sm:px-6" data-testid={`archived-row-${d.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="truncate text-base font-semibold text-dash-ink">{d.address}</div>
                  <div className="mt-1 text-xs text-dash-muted">
                    {d.client_name && <>Client · <span className="text-dash-ink">{d.client_name}</span></>}
                    {d.other_party_name && (
                      <>
                        {" · "}
                        {d.deal_type === DealType.SELL ? "Buyer" : d.deal_type === DealType.BUY ? "Seller" : "Other side"} ·{" "}
                        <span className="text-dash-ink">{d.other_party_name}</span>
                      </>
                    )}
                    {d.archived_at && (
                      <>
                        {" · "}{d.cancelled_at ? "Cancelled" : "Archived"}{" "}
                        {new Date(d.archived_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                        {d.archived_by_name && <> by <span className="text-dash-ink">{d.archived_by_name}</span></>}
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="dashboardOutline"
                    onClick={() => restore(d)}
                    className="!rounded-xl"
                    data-testid={`archived-restore-${d.id}`}
                  >
                    <ArchiveRestore data-icon="inline-start" /> Restore
                  </Button>
                  <Button variant="dashboardOutline"
                    onClick={() => setConfirmTarget(d)}
                    className="!rounded-xl border-dash-rust/40 text-dash-rust hover:bg-dash-rust hover:text-white"
                    data-testid={`archived-delete-${d.id}`}
                  >
                    <Trash2 data-icon="inline-start" /> Delete permanently
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}

      {confirmTarget && (
        <ConfirmDeleteModal
          deal={confirmTarget}
          onClose={() => setConfirmTarget(null)}
          onDeleted={() => {
            setConfirmTarget(null);
            load();
          }}
        />
      )}
    </div>
  );
}


function ConfirmDeleteModal({ deal, onClose, onDeleted }) {
  const expected = (deal.address || "").trim();
  const { mutateAsync: deleteDeal } = useDeleteDeal();
  const submit = async (confirmation) => {
    await deleteDeal({ dealId: deal.id, confirm: confirmation });
    onDeleted?.();
  };

  return (
    <ConfirmDialog open onOpenChange={(nextOpen) => { if (!nextOpen) onClose(); }} title="Delete this deal permanently?" description={<>This can’t be undone. Every task, document, activity entry, and message tied to <strong>{expected}</strong> will be erased.</>} expectedText={expected} confirmationLabel="Type the property address to confirm" confirmLabel="Delete permanently" busyLabel="Deleting…" onConfirm={submit} testId="delete-deal-modal" />
  );
}
